import { Radio, Tooltip } from "antd"
import { formatDimensionEntries } from "helpers/modelNumberMatch.helper"
import { splitCategoryPath } from "helpers/modelNumberCatalogue.helper"
import { ModelNumberPlaceholderIcon } from "components/ui/icons/modelNumber/ModelNumberPlaceholderIcon"
import {
	defaultTooltipStyles,
	getShadowTooltipContainer,
} from "components/ui/customWebviewer/shared/shadowTooltip"
import type { TCategoryPath, TSelectableModelNumber } from "types/modelNumbers.type"
import type { TMatchResult } from "helpers/modelNumberMatch.helper"
import style from "./ModelNumberRow.module.scss"

const {
	row,
	rowTall,
	rowSelected,
	radio,
	thumbnail,
	thumbnailPlaceholder,
	body,
	titleLine,
	modelNumber,
	dims,
	dimCell,
	dimLabel,
	dimValue,
	dimValueOut,
	path,
	pathDot,
	pathAncestors,
	pathLeaf,
	pathAbsent,
	tags,
	tag,
	tagMl,
	tagBest,
	tagPartial,
	tagNone,
	tagNoPrice,
	tagNoCategory,
} = style

type Props = {
	model: TSelectableModelNumber
	isSelected: boolean
	isMlChoice: boolean
	/** Omitted where there is no target to match dimensions against (e.g. the base-model-number picker). */
	match?: TMatchResult
	hasPrice?: boolean
	thumbnailUrl?: string | null
	onSelect: (id: number) => void
	/**
	 * Renders the third line (the three-level category path) and grows the row
	 * 64 px → 82 px. On in the catalogue-wide scope only; the object-category
	 * scope keeps today's two-line row untouched.
	 */
	showCategoryPath?: boolean
	/** Resolved path of the model number. `null` renders the "not assigned" line. */
	categoryPath?: TCategoryPath | null
	/** Amber dot on the path line: selecting this row would move the object's category. */
	isCategoryChange?: boolean
	/** Appended to the row's own class, so a caller can override spacing (e.g. wider horizontal padding) without touching this component's default. */
	className?: string
}

const TAG_TOOLTIPS = {
	ml: "Recommended by the machine learning model",
	best: "All dimensions (W, H, D) fall within the entered range",
	partial: "One or more dimensions are outside the target range",
	none: "This model does not meet the entered cabinet dimensions",
	noPrice: "No pricing data. Contact admin before adding to quotation",
	noCategory: "This model number isn't assigned to a category. Contact admin before using it.",
}

export const ModelNumberRow = ({
	model,
	isSelected,
	isMlChoice,
	match,
	hasPrice = true,
	thumbnailUrl = null,
	onSelect,
	showCategoryPath = false,
	categoryPath = null,
	isCategoryChange = false,
	className = "",
}: Props) => {
	const dimsCells = [
		{ label: "W:", value: formatDimensionEntries(model.dimensions.width), inRange: match?.inRange.width ?? true },
		{ label: "H:", value: formatDimensionEntries(model.dimensions.height), inRange: match?.inRange.height ?? true },
		{ label: "D:", value: formatDimensionEntries(model.dimensions.depth), inRange: match?.inRange.depth ?? true },
	]
	const hasNoCategory = showCategoryPath && categoryPath === null
	const pathParts = categoryPath ? splitCategoryPath(categoryPath) : null

	// Tag order is fixed: No category → No price → ML choice → match. Warnings sit
	// leftmost and exactly one match tag is always present and always rightmost, so
	// the right edge of the list stays scannable.
	return (
		<div
			className={`${row} ${showCategoryPath ? rowTall : ""} ${isSelected ? rowSelected : ""} ${className}`}
			onClick={() => onSelect(model.id)}
		>
			<Radio className={radio} checked={isSelected} value={model.id} />
			{thumbnailUrl ? (
				<img className={thumbnail} src={thumbnailUrl} alt="" />
			) : (
				<span className={thumbnailPlaceholder}>
					<ModelNumberPlaceholderIcon />
				</span>
			)}
			<div className={body}>
				<div className={titleLine}>
					<span className={modelNumber}>{model.model_number}</span>
					<div className={tags}>
						{hasNoCategory ? (
							<Tooltip title={TAG_TOOLTIPS.noCategory} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
								<span className={`${tag} ${tagNoCategory}`}>No category</span>
							</Tooltip>
						) : null}
						{!hasPrice ? (
							<Tooltip title={TAG_TOOLTIPS.noPrice} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
								<span className={`${tag} ${tagNoPrice}`}>No price</span>
							</Tooltip>
						) : null}
						{isMlChoice ? (
							<Tooltip title={TAG_TOOLTIPS.ml} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
								<span className={`${tag} ${tagMl}`}>ML choice</span>
							</Tooltip>
						) : null}
						{match?.tag === "best" ? (
							<Tooltip title={TAG_TOOLTIPS.best} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
								<span className={`${tag} ${tagBest}`}>Best match</span>
							</Tooltip>
						) : null}
						{match?.tag === "partial" ? (
							<Tooltip title={TAG_TOOLTIPS.partial} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
								<span className={`${tag} ${tagPartial}`}>Partial match</span>
							</Tooltip>
						) : null}
						{match?.tag === "none" ? (
							<Tooltip title={TAG_TOOLTIPS.none} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
								<span className={`${tag} ${tagNone}`}>No match</span>
							</Tooltip>
						) : null}
					</div>
				</div>
				<div className={dims}>
					{dimsCells.map((cell) => (
						<span key={cell.label} className={dimCell}>
							<span className={dimLabel}>{cell.label}</span>
							{/* Out-of-range axes are red in EVERY scope, including the catalogue-wide one:
							    the red value is how the estimator spots which axis is off without
							    reading the match tag, and suppressing it there made a cross-category
							    row look like a clean match. */}
							<span className={cell.inRange ? dimValue : dimValueOut}>{cell.value}</span>
						</span>
					))}
				</div>
				{showCategoryPath ? (
					pathParts ? (
						<div className={path}>
							{isCategoryChange ? <span className={pathDot} /> : null}
							<span className={pathAncestors}>{pathParts.ancestors}</span>
							<span className={pathLeaf}>{pathParts.leaf}</span>
						</div>
					) : (
						<div className={`${path} ${pathAbsent}`}>Not assigned to a category</div>
					)
				) : null}
			</div>
		</div>
	)
}
